import { useMemo } from 'react';
import { NotificationRow } from './NotificationRow';
import { GroupedNotificationRow } from './GroupedNotificationRow';
import { NotificationEmptyState } from './NotificationEmptyState';
import { NotificationListSkeleton } from './NotificationListSkeleton';
import { NotificationErrorState } from './NotificationErrorState';
import { groupConsecutive } from '../../utils/notificationGrouping';

/**
 * Renders a list of notifications with built-in loading/error/empty handling,
 * consecutive-grouping, and an optional focused-row highlight (for keyboard
 * navigation).
 *
 * Props:
 *   items:      Notification[]           — already filtered by the caller
 *   loading:    boolean                  — show skeleton when true and items empty
 *   error:      unknown | null           — show error block when truthy
 *   onRetry:    () => void               — required when `error` may be set
 *   compact:    boolean                  — pass-through to rows
 *   onRowClick: () => void               — pass-through, used by the dropdown
 *                                          to close itself after navigation
 *   emptyVariant: 'default' | 'unread' | 'action'
 *   focusedIndex: number | null          — index into the FLAT items array
 *                                          (not the grouped output) to
 *                                          highlight; null for none
 */
export function NotificationList({
  items,
  loading,
  error,
  onRetry,
  compact = false,
  onRowClick,
  emptyVariant = 'default',
  focusedIndex = null,
}) {
  const groups = useMemo(() => groupConsecutive(items ?? []), [items]);

  if (error) {
    return <NotificationErrorState onRetry={onRetry} compact={compact} />;
  }
  if (loading && (!items || items.length === 0)) {
    return <NotificationListSkeleton compact={compact} />;
  }
  if (!items || items.length === 0) {
    return <NotificationEmptyState variant={emptyVariant} compact={compact} />;
  }

  // We need to map back from the flat index (used by keyboard nav) to the
  // group + sub-index inside it.
  let flatCursor = 0;
  return (
    <div>
      {groups.map((group) => {
        const start = flatCursor;
        flatCursor += group.items.length;
        if (!group.isGroup) {
          const n = group.items[0];
          const isFocused = focusedIndex === start;
          return (
            <FocusableRow key={group.id} focused={isFocused}>
              <NotificationRow notification={n} compact={compact} onClick={onRowClick} />
            </FocusableRow>
          );
        }
        const groupFocused =
          focusedIndex !== null && focusedIndex >= start && focusedIndex < start + group.items.length;
        return (
          <GroupedNotificationRow
            key={group.id}
            group={group}
            compact={compact}
            onRowClick={onRowClick}
            focused={groupFocused}
          />
        );
      })}
    </div>
  );
}

function FocusableRow({ focused, children }) {
  return (
    <div className={focused ? 'ring-2 ring-inset ring-primary-500 dark:ring-carbon-400' : ''}>
      {children}
    </div>
  );
}
